import React, { useEffect, useState } from 'react';
import { Button, Card, Descriptions, Spin, message, Tag, Divider, Modal, Form, Input, DatePicker, Select } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api/client';

interface CompanyOrder {
  id: number;
  number: string | null;
  date: string | null;
  title: string;
  content: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Действует',
  cancelled: 'Отменён',
  draft: 'Черновик',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'success',
  cancelled: 'red',
  draft: 'default',
};

const CompanyOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<CompanyOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    if (!id) return;
    setLoading(true);
    api.get(`/company-orders/${id}`)
      .then(r => {
        setOrder(r.data);
        form.setFieldsValue({
          ...r.data,
          date: r.data.date ? dayjs(r.data.date) : null,
        });
      })
      .catch(() => message.error('Ошибка загрузки'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleDelete = () => {
    Modal.confirm({
      title: 'Удалить приказ?',
      content: 'Это действие необратимо.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: () => {
        api.delete(`/company-orders/${id}`)
          .then(() => {
            message.success('Приказ удалён');
            navigate('/company-orders');
          })
          .catch(() => message.error('Ошибка удаления'));
      },
    });
  };

  const handleSave = async (values: any) => {
    const payload = {
      ...values,
      date: values.date ? values.date.format('YYYY-MM-DD') : null,
    };
    try {
      await api.put(`/company-orders/${id}`, payload);
      message.success('Сохранено');
      setEditOpen(false);
      load();
    } catch {
      message.error('Ошибка сохранения');
    }
  };

  if (loading || !order) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/company-orders')}>
          Назад к списку
        </Button>
        <div>
          <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)} style={{ marginRight: 8 }}>
            Редактировать
          </Button>
          <Button icon={<DeleteOutlined />} danger onClick={handleDelete}>
            Удалить
          </Button>
        </div>
      </div>

      <Card>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Статус">
            <Tag color={STATUS_COLORS[order.status] || 'default'}>
              {STATUS_LABELS[order.status] || order.status}
            </Tag>
          </Descriptions.Item>
          {order.number && <Descriptions.Item label="Номер">{order.number}</Descriptions.Item>}
          {order.date && (
            <Descriptions.Item label="Дата">
              {new Date(order.date).toLocaleDateString('ru-RU')}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Название">{order.title}</Descriptions.Item>
        </Descriptions>
        <Divider />
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.6 }}>
          {order.content}
        </div>
      </Card>

      <Modal
        title="Редактировать приказ"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => form.submit()}
        okText="Сохранить"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="number" label="Номер">
            <Input placeholder="Номер приказа" />
          </Form.Item>
          <Form.Item name="date" label="Дата">
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="title" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="content" label="Текст" rules={[{ required: true, message: 'Введите текст' }]}>
            <Input.TextArea rows={6} />
          </Form.Item>
          <Form.Item name="status" label="Статус" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'active', label: 'Действует' },
                { value: 'cancelled', label: 'Отменён' },
                { value: 'draft', label: 'Черновик' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyOrderDetailPage;
